import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import RelativeTime from '../components/common/RelativeTime';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

type CompetitionMode = 'individual' | 'team';

interface SubmissionRow {
  id: number;
  competition_id: number;
  user_id: number;
  team_id?: number | null;
  team_name?: string | null;
  username?: string | null;
  file_path: string;
  file_type?: string | null;
  version?: number | null;
  status?: string | null;
  submitted_at: string;
  last_synced_at?: string | null;
}

interface EvaluatedEntity {
  entityId: number;
  label: string;
  fileType: string;
  latestVersion: number;
  submittedAt: string;
  submittedAtMs: number;
  lastSyncedAt: string | null;
  lastSyncedAtMs: number | null;
  needsEval: boolean;
  filePath: string;
}

interface ActionRowResult {
  entityId: number;
  label: string;
  success: boolean;
  detail: string;
}

interface ActionRunResult {
  title: string;
  total: number;
  successful: number;
  failed: number;
  rows: ActionRowResult[];
  completedAt: string;
}

interface ManageCompetitionEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
  competitionMode?: CompetitionMode;
}

const ManageCompetitionEvaluationModal: React.FC<ManageCompetitionEvaluationModalProps> = ({
  isOpen,
  onClose,
  competitionId,
  competitionMode = 'individual',
}) => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [lastRun, setLastRun] = useState<ActionRunResult | null>(null);

  const loadSubmissions = useCallback(
    async (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      try {
        const res = await apiService.post(`/evaluations/${competitionId}`, {});
        const rows = Array.isArray(res?.data) ? (res.data as SubmissionRow[]) : [];
        setSubmissions(rows);
      } catch (err: any) {
        console.error('Failed to load submissions for evaluation management:', err);
        setError(err?.message || 'Failed to load submissions');
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [competitionId]
  );

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setQuery('');
    setSelectedIds([]);
    setSubmissions([]);
    setLastRun(null);
    loadSubmissions(true);
  }, [isOpen, loadSubmissions]);

  const entities = useMemo(() => {
    const byEntity = new Map<number, EvaluatedEntity>();

    for (const row of submissions) {
      const entityId = competitionMode === 'team' ? Number(row.team_id) : Number(row.user_id);
      if (!entityId || Number.isNaN(entityId)) continue;

      const submittedAtMs = new Date(row.submitted_at).getTime();
      const rowLastSyncedAt = row.last_synced_at || null;
      const rowLastSyncedAtMs = rowLastSyncedAt ? new Date(rowLastSyncedAt).getTime() : null;
      const latestVersion = Number(row.version || 0);
      const label = competitionMode === 'team'
        ? (row.team_name || `Team ${entityId}`)
        : (row.username || `User ${entityId}`);
      const fileType = row.file_type || 'unknown';
      const existing = byEntity.get(entityId);
      const nextLastSyncedAtMs = (() => {
        if (!existing?.lastSyncedAtMs && !rowLastSyncedAtMs) return null;
        if (!existing?.lastSyncedAtMs) return rowLastSyncedAtMs;
        if (!rowLastSyncedAtMs) return existing.lastSyncedAtMs;
        return Math.max(existing.lastSyncedAtMs, rowLastSyncedAtMs);
      })();
      const nextLastSyncedAt = (() => {
        if (!nextLastSyncedAtMs) return null;
        if (rowLastSyncedAtMs === nextLastSyncedAtMs && rowLastSyncedAt) return rowLastSyncedAt;
        return existing?.lastSyncedAt || null;
      })();

      const shouldReplaceSubmission =
        !existing ||
        latestVersion > existing.latestVersion ||
        (latestVersion === existing.latestVersion && submittedAtMs > existing.submittedAtMs);

      if (shouldReplaceSubmission) {
        byEntity.set(entityId, {
          entityId,
          label,
          fileType,
          latestVersion,
          submittedAt: row.submitted_at,
          submittedAtMs,
          lastSyncedAt: nextLastSyncedAt,
          lastSyncedAtMs: nextLastSyncedAtMs,
          needsEval: false,
          filePath: row.file_path,
        });
      } else if (existing) {
        byEntity.set(entityId, {
          ...existing,
          lastSyncedAt: nextLastSyncedAt,
          lastSyncedAtMs: nextLastSyncedAtMs,
        });
      }
    }

    return Array.from(byEntity.values())
      .map((entity) => ({
        ...entity,
        needsEval: !entity.lastSyncedAtMs || entity.submittedAtMs > entity.lastSyncedAtMs,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [competitionMode, submissions]);

  useEffect(() => {
    const validIds = new Set(entities.map((e) => e.entityId));
    setSelectedIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [entities]);

  const filteredEntities = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter((entity) => {
      const haystack = `${entity.label} ${entity.fileType} ${entity.filePath}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [entities, query]);

  useEscapeToClose(onClose, { enabled: isOpen && !actionLoading });

  if (!isOpen) return null;

  const resolveErrorMessage = (err: any): string => {
    if (err?.response?.data?.detail) return String(err.response.data.detail);
    if (err?.message) return String(err.message);
    if (typeof err === 'string') return err;
    return 'Request failed';
  };

  const upsertSelection = (entityId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(entityId) ? prev : [...prev, entityId];
      }
      return prev.filter((id) => id !== entityId);
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const set = new Set(prev);
      for (const entity of filteredEntities) set.add(entity.entityId);
      return Array.from(set);
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const normalizeBatchRows = (
    rawRows: any[],
    title: string,
    defaultSuccessDetail: string
  ): ActionRunResult => {
    const normalized: ActionRowResult[] = rawRows.map((raw) => {
      const entityId = Number(raw?.team_id ?? raw?.user_id);
      const label = raw?.team_name || raw?.username || `Entity ${entityId}`;
      const success = Boolean(raw?.success);
      let detail = success ? defaultSuccessDetail : (raw?.error || 'Failed');

      if (success && typeof raw?.score === 'number') {
        detail = `Score: ${raw.score}`;
      }

      if (success && raw?.extras?.mode === 'external_job') {
        detail = 'External job queued';
      }

      if (success && raw?.job_id) {
        detail = `Synced (job_id: ${raw.job_id})`;
      }

      return { entityId, label, success, detail };
    });

    const successful = normalized.filter((row) => row.success).length;
    const failed = normalized.length - successful;
    return {
      title,
      total: normalized.length,
      successful,
      failed,
      rows: normalized,
      completedAt: new Date().toISOString(),
    };
  };

  const runEvaluateAll = async () => {
    setError('');
    setActionLoading(true);
    try {
      const res = await apiService.post(`/evaluations/${competitionId}/evaluate_all`, {});
      const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
      setLastRun(normalizeBatchRows(rows, 'Evaluate All', 'Evaluated'));
      await loadSubmissions(false);
    } catch (err: any) {
      setError(resolveErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const runSyncAll = async () => {
    setError('');
    setActionLoading(true);
    try {
      const res = await apiService.post(`/evaluations/${competitionId}/sync_external_all`, {});
      const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
      setLastRun(normalizeBatchRows(rows, 'Sync External All', 'Synced'));
      await loadSubmissions(false);
    } catch (err: any) {
      setError(resolveErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const runSelected = async (mode: 'evaluate' | 'sync') => {
    setError('');
    if (selectedIds.length === 0) {
      setError('Select at least one submission');
      return;
    }

    setActionLoading(true);
    try {
      const entitiesById = new Map(entities.map((entity) => [entity.entityId, entity]));
      const selectedEntities = selectedIds
        .map((id) => entitiesById.get(id))
        .filter((entity): entity is EvaluatedEntity => Boolean(entity));

      const rows: ActionRowResult[] = await Promise.all(
        selectedEntities.map(async (entity) => {
          const endpoint = mode === 'evaluate'
            ? `/evaluations/${competitionId}/evaluate/${entity.entityId}`
            : `/evaluations/${competitionId}/sync_external/${entity.entityId}`;
          try {
            const res = await apiService.post(endpoint, {});
            const data = res?.data || {};
            let detail = mode === 'evaluate' ? 'Evaluated' : 'Synced';
            if (mode === 'evaluate' && data?.mode === 'external_job') {
              detail = 'External job queued';
            } else if (typeof data?.score === 'number') {
              detail = `Score: ${data.score}`;
            } else if (mode === 'sync' && data?.job_id) {
              detail = `Synced (job_id: ${data.job_id})`;
            }
            return {
              entityId: entity.entityId,
              label: entity.label,
              success: true,
              detail,
            };
          } catch (err: any) {
            return {
              entityId: entity.entityId,
              label: entity.label,
              success: false,
              detail: resolveErrorMessage(err),
            };
          }
        })
      );

      const successful = rows.filter((row) => row.success).length;
      const failed = rows.length - successful;
      setLastRun({
        title: mode === 'evaluate' ? 'Evaluate Selected' : 'Sync External Selected',
        total: rows.length,
        successful,
        failed,
        rows,
        completedAt: new Date().toISOString(),
      });
      await loadSubmissions(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="register-modal-backdrop" onClick={handleBackdropClick}>
      <div className="register-modal manage-eval-modal" role="dialog" aria-modal="true">
        <div className="register-modal__header">
          <h2 className="register-modal__title">Auto Evaluation & External Sync</h2>
          <button type="button" className="register-modal__close" onClick={onClose} aria-label="Close modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="register-modal__form">
          {error && (
            <div className="register-modal__error" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <LoadingSpinner text="Loading latest submissions..." />
          ) : (
            <>
              <div className="register-modal__fields">
                <p className="manage-eval-modal__intro">
                  Select submissions to evaluate or sync external results. Actions use the latest submission per
                  {competitionMode === 'team' ? ' team' : ' user'}.
                </p>

                <div className="manage-eval-modal__toolbar">
                  <Input
                    name="query"
                    label="Search submissions"
                    placeholder={competitionMode === 'team' ? 'Search by team or file path' : 'Search by user or file path'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={actionLoading}
                  />
                  <div className="manage-eval-modal__toolbar-meta">
                    <span>
                      Showing <strong>{filteredEntities.length}</strong> of <strong>{entities.length}</strong>
                    </span>
                    <span>
                      Selected <strong>{selectedIds.length}</strong>
                    </span>
                  </div>
                  <div className="manage-eval-modal__toolbar-actions">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllVisible} disabled={actionLoading || filteredEntities.length === 0}>
                      Select Visible
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearSelection} disabled={actionLoading || selectedIds.length === 0}>
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="manage-eval-modal__table-wrap">
                  <table className="manage-eval-modal__table">
                    <thead>
                      <tr>
                        <th aria-label="Select" />
                        <th>{competitionMode === 'team' ? 'Team' : 'User'}</th>
                        <th>File Type</th>
                        <th>Version</th>
                        <th>Submitted</th>
                        <th>Last Synced</th>
                        <th>Needs Eval</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEntities.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="manage-eval-modal__empty">No submissions found.</td>
                        </tr>
                      ) : (
                        filteredEntities.map((entity) => (
                          <tr key={entity.entityId}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(entity.entityId)}
                                onChange={(e) => upsertSelection(entity.entityId, e.target.checked)}
                                disabled={actionLoading}
                              />
                            </td>
                            <td>{entity.label}</td>
                            <td>{entity.fileType}</td>
                            <td>{entity.latestVersion || '-'}</td>
                            <td><RelativeTime value={entity.submittedAt} fallback="Never" /></td>
                            <td><RelativeTime value={entity.lastSyncedAt} fallback="Never" /></td>
                            <td>
                              <span className={`manage-eval-status-pill ${entity.needsEval ? 'is-needs' : 'is-up'}`}>
                                {entity.needsEval ? 'Yes' : 'No'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="manage-eval-modal__actions-grid">
                <div className="manage-eval-modal__action-group">
                  <h4>Evaluate</h4>
                  <div className="manage-eval-modal__action-row">
                    <Button type="button" variant="primary" onClick={runEvaluateAll} disabled={actionLoading || entities.length === 0}>
                      Evaluate All
                    </Button>
                    <Button type="button" variant="outline" onClick={() => runSelected('evaluate')} disabled={actionLoading || selectedIds.length === 0}>
                      Evaluate Selected
                    </Button>
                  </div>
                </div>

                <div className="manage-eval-modal__action-group">
                  <h4>External Sync</h4>
                  <div className="manage-eval-modal__action-row">
                    <Button type="button" variant="primary" onClick={runSyncAll} disabled={actionLoading || entities.length === 0}>
                      Sync All
                    </Button>
                    <Button type="button" variant="outline" onClick={() => runSelected('sync')} disabled={actionLoading || selectedIds.length === 0}>
                      Sync Selected
                    </Button>
                  </div>
                </div>
              </div>

              {actionLoading && (
                <div className="manage-eval-modal__loading">
                  <LoadingSpinner text="Running action..." />
                </div>
              )}

              {lastRun && !actionLoading && (
                <div className="manage-eval-modal__result">
                  <div className="manage-eval-modal__result-summary">
                    <strong>{lastRun.title}</strong>
                    <span><RelativeTime value={lastRun.completedAt} /></span>
                    <span>Total: {lastRun.total}</span>
                    <span>Success: {lastRun.successful}</span>
                    <span>Failed: {lastRun.failed}</span>
                  </div>

                  <div className="manage-eval-modal__result-rows">
                    {lastRun.rows.map((row) => (
                      <div
                        key={`${lastRun.completedAt}-${row.entityId}`}
                        className={`manage-eval-modal__result-row ${row.success ? 'is-success' : 'is-fail'}`}
                      >
                        <strong>{row.label}</strong>
                        <span>{row.detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="register-modal__actions">
                <Button type="button" variant="secondary" onClick={onClose} disabled={actionLoading}>
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCompetitionEvaluationModal;
